import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
  Navigate,
} from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  useMediaQuery,
  useTheme,
  ThemeProvider,
  CssBaseline,
} from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import CartDialog from './components/CartDialog';
import ErrorBoundary from './components/ErrorBoundary';
import ProtectedRoute from '@components/auth/ProtectedRoute';
import ThemeBranding from './components/ThemeBranding';

import TopBar from './components/layout/TopBar';
import ContextBar from './components/layout/ContextBar';
import Sidebar from './components/layout/Sidebar';
import SkipNavigation from './components/SkipNavigation';
import HeaderImage from './components/HeaderImage';
import OrderSummaryDialog from './components/OrderSummaryDialog';
import VenueOrderSummaryDialog from './components/VenueOrderSummaryDialog';
import { useTranslation } from 'react-i18next';
import client from '@api/client';
import appTheme, { createAppTheme } from './theme';
import { isRtlLanguage } from './i18n/rtl';
import { ORDER_API, VENUE_API } from './api/endpoints';
import useAuthStore from './stores/authStore';
import useOrderStore from './stores/orderStore';
import useUiStore from './stores/uiStore';
import { storage } from './utils/storage';

// Phase 1 — new pages
const LoginPage = React.lazy(() => import('./features/management/pages/LoginPage'));
const OrderManagement = React.lazy(() => import('./features/management/pages/OrderManagement'));
const KitchenDisplay = React.lazy(() => import('./features/kitchen/pages/KitchenDisplay'));
const PickupDisplay = React.lazy(() => import('./features/kitchen/pages/PickupDisplay'));
const TableManagement = React.lazy(() => import('./features/management/pages/TableManagement'));
const NotFound = React.lazy(() => import('./features/management/pages/NotFound'));

// Phase 2 — Customer-facing ordering
const CustomerShell = React.lazy(() => import('./features/ordering/layouts/CustomerShell'));
const TableOrdering = React.lazy(() => import('./features/ordering/pages/TableOrdering'));
const KioskOrdering = React.lazy(() => import('./features/ordering/pages/KioskOrdering'));
const VenueKioskOrdering = React.lazy(() => import('./features/ordering/pages/VenueKioskOrdering'));
const CounterOrdering = React.lazy(() => import('./features/ordering/pages/CounterOrdering'));
const VenueOrdering = React.lazy(() => import('./features/ordering/pages/VenueOrdering'));

// Phase 3 — Management & Analytics
const Dashboard = React.lazy(() => import('./features/management/pages/Dashboard'));
const Reports = React.lazy(() => import('./features/management/pages/Reports'));
const MenuManagement = React.lazy(() => import('./features/management/pages/MenuManagement'));
const UserManagement = React.lazy(() => import('./features/management/pages/UserManagement'));

const RestaurantSettings = React.lazy(() => import('./features/management/pages/RestaurantSettings'));

// Phase 4 — Features & Integrations
const ReservationCalendar = React.lazy(() => import('./features/reservations/pages/ReservationCalendar'));
const ReservationWidget = React.lazy(() => import('./features/reservations/pages/ReservationWidget'));
const ReserveLandingPage = React.lazy(() => import('./features/reservations/pages/ReserveLandingPage'));
const ServiceRequestDashboard = React.lazy(() => import('./features/management/pages/ServiceRequestDashboard'));
const RatingManagement = React.lazy(() => import('./features/management/pages/RatingManagement'));
const VenueQRCodeManagement = React.lazy(() => import('./features/management/pages/VenueQRCodeManagement'));
const QRCodeManagement = React.lazy(() => import('./features/management/pages/QRCodeManagement'));

// Phase 5 — Tier 1: Customer experience & payments
const CustomerOrderHistory = React.lazy(() => import('./features/ordering/pages/CustomerOrderHistory'));
const CustomerAccount = React.lazy(() => import('./features/ordering/pages/CustomerAccount'));
const CustomerFavorites = React.lazy(() => import('./features/ordering/pages/CustomerFavorites'));
const CustomerLoyalty = React.lazy(() => import('./features/ordering/pages/CustomerLoyalty'));
const ReservationManage = React.lazy(() => import('./features/reservations/pages/ReservationManage'));
const PaymentManagement = React.lazy(() => import('./features/management/pages/PaymentManagement'));

// Phase 6 — Tier 2: SYSTEM admin pages
const VenueManagement = React.lazy(() => import('./features/management/pages/VenueManagement'));
const VenueOrderManagement = React.lazy(() => import('./features/management/pages/VenueOrderManagement'));
const VenuePickupDisplay = React.lazy(() => import('./features/kitchen/pages/VenuePickupDisplay'));
const VenueDashboard = React.lazy(() => import('./features/management/pages/VenueDashboard'));
const RestaurantManagement = React.lazy(() => import('./features/management/pages/RestaurantManagement'));
const RestaurantTypeManagement = React.lazy(() => import('./features/management/pages/RestaurantTypeManagement'));

const VenueSettings = React.lazy(() => import('./features/management/pages/VenueSettings'));
const VenueRestaurantsPage = React.lazy(() => import('./features/management/pages/VenueRestaurantsPage'));
const VenueStaffPage = React.lazy(() => import('./features/management/pages/VenueStaffPage'));
const SystemUserManagement = React.lazy(() => import('./features/management/pages/SystemUserManagement'));
const ResetPassword = React.lazy(() => import('./features/management/pages/ResetPassword'));
const LandingPage = React.lazy(() => import('./features/management/pages/LandingPage'));
const RestaurantPickerPage = React.lazy(() => import('./features/management/pages/RestaurantPickerPage'));
const VenuePickerPage = React.lazy(() => import('./features/management/pages/VenuePickerPage'));
const WorkspacePickerPage = React.lazy(() => import('./features/management/pages/WorkspacePickerPage'));

function RootRedirect() {
  const token = useAuthStore((s) => s.token);
  return <Navigate to={token ? '/dashboard' : '/login'} replace />;
}

function RouteContextSync() {
  const { pathname } = useLocation();
  const setActiveRestaurant = useAuthStore((s) => s.setActiveRestaurant);
  const setActiveVenue = useAuthStore((s) => s.setActiveVenue);
  const rehydrateContextFromMemberships = useAuthStore(
    (s) => s.rehydrateContextFromMemberships,
  );
  const storeRestaurantId = useAuthStore((s) => s.restaurantId);
  const storeVenueId = useAuthStore((s) => s.venueId);

  // Use refs so the effect only fires on pathname changes, not store changes.
  // Without this, clearing restaurantId in returnToVenue() triggers the effect
  // while the URL still shows /restaurant/…, causing setActiveRestaurant to
  // immediately re-set the id that was just cleared.
  const storeRestaurantIdRef = useRef(storeRestaurantId);
  storeRestaurantIdRef.current = storeRestaurantId;
  const storeVenueIdRef = useRef(storeVenueId);
  storeVenueIdRef.current = storeVenueId;

  useEffect(() => {
    rehydrateContextFromMemberships();

    const restaurantMatch = pathname.match(/^\/restaurant\/([^/]+)\//);
    if (restaurantMatch) {
      const urlRestaurantId = restaurantMatch[1];
      if (urlRestaurantId !== storeRestaurantIdRef.current) {
        setActiveRestaurant(urlRestaurantId);
      }
      return;
    }

    const venueMatch = pathname.match(/^\/venue\/([^/]+)\//);
    if (venueMatch) {
      const urlVenueId = venueMatch[1];
      if (urlVenueId !== storeVenueIdRef.current) {
        setActiveVenue(urlVenueId);
      }
    }
  }, [pathname, rehydrateContextFromMemberships, setActiveRestaurant, setActiveVenue]);

  return null;
}

function ConnectionBanner() {
  const { t } = useTranslation('common');
  const backendStatus = useUiStore((s) => s.backendStatus);
  const backendStatusMessage = useUiStore((s) => s.backendStatusMessage);
  const clearBackendError = useUiStore((s) => s.clearBackendError);
  const intervalRef = useRef(null);

  const checkHealth = useCallback(async () => {
    try {
      await fetch(import.meta.env.VITE_API_BASE_URL, { method: 'HEAD' });
      clearBackendError();
    } catch {
      // still unreachable
    }
  }, [clearBackendError]);

  useEffect(() => {
    if (backendStatus === 'unreachable') {
      intervalRef.current = setInterval(checkHealth, 30000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [backendStatus, checkHealth]);

  if (backendStatus === 'connected') return null;

  return (
    <Alert
      severity="warning"
      variant="filled"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        borderRadius: 0,
      }}
      action={
        <Button color="inherit" size="small" onClick={checkHealth}>
          {t('connection.check', 'Erneut prüfen')}
        </Button>
      }
    >
      {backendStatusMessage}
    </Alert>
  );
}

function AppShell() {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const token = useAuthStore((s) => s.token);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const user = useAuthStore((s) => s.user);
  const memberships = useAuthStore((s) => s.memberships);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const setAuth = useAuthStore((s) => s.setAuth);

  // Zustand stores (only reads needed by AppShell itself)
  const orderSummary = useOrderStore((s) => s.orderSummary);
  const setOrderSummary = useOrderStore((s) => s.setOrderSummary);
  const setVenueOrderSummary = useOrderStore((s) => s.setVenueOrderSummary);

  const navOpen = useUiStore((s) => s.navOpen);
  const setNavOpen = useUiStore((s) => s.setNavOpen);

  const { pathname } = useLocation();
  const profileHydratedRef = useRef(false);

  // Pages where the header should NOT be shown
  const hideHeaderOn = [
    '/',
    '/kiosk',
    '/venue-kiosk',
    '/counter',
    '/kitchen-display',
    '/pickup-display',
    '/venue-pickup',
    '/login',
    '/reset-password',
    '/venuesordner',
    '/order',
    '/reserve',
    '/discover',
    '/modules',
    '/landingpage',
    '/select-restaurant',
    '/select-venue',
    '/select-workspace',
  ];

  const hideHeroOn = [
    '/',
    '/counter',
    '/kitchen-display',
    '/pickup-display',
    '/kiosk',
    '/venue-kiosk',
    '/login',
    '/landingpage',
    '/dashboard',
    '/auswertungen',
    '/restaurant-settings',
    '/orders',
    '/menue',
    '/tables',
    '/venuesordner',
    '/order',
    '/reservations',
    '/service-requests',
    '/ratings',
    '/reserve',
    '/discover',
    '/modules',
    '/payments',
    '/venues',
    '/restaurants',
    '/restaurant',
    '/restaurant-types',
    '/venue-pickup',
    '/venue',
    '/users',
    '/usermanagement',
  ];

  // Match exact path or path with trailing slash (prevents '/order' from matching '/orders')
  const matchesRoute = (route, path) => path === route || path.startsWith(route + '/');
  const showHeader = !hideHeaderOn.some((p) => matchesRoute(p, pathname));
  const showHeaderImage = !hideHeroOn.some((p) => matchesRoute(p, pathname));
  const contentShift = showHeader && !isMobile ? (navOpen ? 260 : 72) : 0;
  const headerHeight = 72;
  const venueId = useAuthStore((s) => s.venueId);
  const contextBarHeight = showHeader && !!venueId ? 40 : 0;

  useEffect(() => {
    const needsProfileRehydrate =
      isAuthenticated &&
      !!token &&
      user?.accountType !== 'SYSTEM' &&
      (!Array.isArray(memberships) || memberships.length === 0);

    if (!needsProfileRehydrate || profileHydratedRef.current) return;
    profileHydratedRef.current = true;

    const syncProfile = async () => {
      try {
        const res = await client.get('/users/me');
        const profile = res?.data?.data ?? res?.data;
        if (!profile) return;
        setAuth({
          token,
          refreshToken: refreshToken || null,
          user: profile,
          restaurantName: null,
        });
      } catch {
        profileHydratedRef.current = false;
      }
    };

    syncProfile();
  }, [isAuthenticated, memberships, refreshToken, setAuth, token, user?.accountType]);

  useEffect(() => {
    const fetchOrderFromStorage = async () => {
      const storedOrderId = storage.getCurrentOrderId();
      if (!storedOrderId) return;

      try {
        const response = await client.get(`${ORDER_API}/${storedOrderId}`);
        if (response.status === 200) {
          const data = response.data;
          if (data.status === 'PLACED') {
            const summary = {
              status: data.status,
              orderId: data.id,
              tableId: data.tableId,
              orderNumber: data.orderNumber,
              customerName: data.customerName,
              customerPhone: data.customerPhone,
              specialInstructions: data.specialInstructions,
              subtotal: data.subtotal,
              tax: data.tax,
              total: data.total,
              placedAt: data.placedAt,
              restaurantId: data.restaurantId,
              items: data.orderItems.map((item) => ({
                name: item.menuItem.name,
                quantity: item.quantity,
                total: item.totalPrice,
                notes: item.specialNotes,
                components:
                  item.components?.map((c) => ({
                    value: c.value,
                    optionName: c.optionName,
                  })) || [],
              })),
            };
            setOrderSummary(summary);
          }
        }
      } catch (error) {
        // error silenced
      }
    };
    fetchOrderFromStorage();
  }, [setOrderSummary]);

  useEffect(() => {
    if (!showHeader) setNavOpen(false);
    else if (!isMobile) setNavOpen(true);
  }, [setNavOpen, showHeader, isMobile]);

  useEffect(() => {
    const fetchVenueOrderFromStorage = async () => {
      const orderMode = storage.getOrderMode();
      const venueId = storage.getVenueId();
      const storedVenueOrderId = storage.getCurrentVenueOrderId();
      if (orderMode !== 'VENUE' || !venueId || !storedVenueOrderId) return;

      try {
        const response = await client.get(
          `${VENUE_API}/${venueId}/orders/${storedVenueOrderId}`,
        );
        if (response.status === 200) {
          setVenueOrderSummary(response.data);
        }
      } catch (error) {
        // error silenced
      }
    };
    fetchVenueOrderFromStorage();
  }, [setVenueOrderSummary]);


  return (
    <>
      <RouteContextSync />
      <SkipNavigation />
      <ConnectionBanner />
      {showHeader && <ContextBar height={contextBarHeight} />}
      {showHeader && (
        <Sidebar
          open={navOpen}
          onClose={() => setNavOpen(false)}
          onToggle={() => setNavOpen(!navOpen)}
          topOffset={contextBarHeight}
        />
      )}
      <Box
        sx={{
          marginLeft: `${contentShift}px`,
          width: `calc(100% - ${contentShift}px)`,
          transition: 'margin-left 0.2s ease, width 0.2s ease',
          minHeight: '100vh',
          pt: showHeader ? `${headerHeight + contextBarHeight}px` : 0,
        }}
      >
        {showHeader && <TopBar onMenuToggle={() => setNavOpen(true)} topOffset={contextBarHeight} />}
        {showHeaderImage && <HeaderImage />}

        <Suspense
          fallback={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                mt: 8,
              }}
            >
              <CircularProgress />
            </Box>
          }
        >
        <Box component="main" id="main-content" tabIndex={-1} sx={{ outline: 'none' }}>
        <ErrorBoundary>
        <Routes>
          <Route path="/" element={<RootRedirect />} />

          {/* Auth */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* Restaurant picker for multi-restaurant staff */}
          <Route
            path="/select-restaurant"
            element={
              <ProtectedRoute>
                <RestaurantPickerPage />
              </ProtectedRoute>
            }
          />

          {/* Venue picker for multi-venue staff */}
          <Route
            path="/select-venue"
            element={
              <ProtectedRoute>
                <VenuePickerPage />
              </ProtectedRoute>
            }
          />

          {/* Unified workspace picker — for users with both restaurant + venue memberships */}
          <Route
            path="/select-workspace"
            element={
              <ProtectedRoute>
                <WorkspacePickerPage />
              </ProtectedRoute>
            }
          />

          {/* Phase 1 — Core operational views */}
          <Route
            path="/orders"
            element={
              <ProtectedRoute requiredRole="CASHIER">
                <OrderManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/kitchen-display"
            element={
              <ProtectedRoute requiredRole="KITCHEN_STAFF">
                <KitchenDisplay />
              </ProtectedRoute>
            }
          />
          <Route
            path="/pickup-display"
            element={
              <ProtectedRoute requiredRole="KITCHEN_STAFF">
                <PickupDisplay />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tables"
            element={
              <ProtectedRoute requiredRole="WAITER">
                <TableManagement />
              </ProtectedRoute>
            }
          />

          {/* Dashboard & analytics */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute requiredRole="CASHIER">
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/auswertungen"
            element={
              <ProtectedRoute requiredRole="MANAGER">
                <Reports />
              </ProtectedRoute>
            }
          />

          {/* Settings & management */}
          <Route path="/openinghours" element={<Navigate to="/restaurant-settings?tab=hours" replace />} />
          <Route path="/einstellungen" element={<Navigate to="/restaurant-settings" replace />} />
          <Route
            path="/restaurant-settings"
            element={
              <ProtectedRoute requiredRole="MANAGER">
                <RestaurantSettings />
              </ProtectedRoute>
            }
          />
          <Route path="/payment-settings" element={<Navigate to="/restaurant-settings?tab=payment" replace />} />
          <Route path="/tax-rules" element={<Navigate to="/restaurant-settings?tab=tax" replace />} />
          <Route
            path="/usermanagement"
            element={
              <ProtectedRoute requiredRole="MANAGER">
                <UserManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/users"
            element={
              <ProtectedRoute requiredRole="SYSTEM">
                <SystemUserManagement />
              </ProtectedRoute>
            }
          />

          {/* Menu & categories */}
          <Route
            path="/menue"
            element={
              <ProtectedRoute requiredRole="MANAGER">
                <MenuManagement />
              </ProtectedRoute>
            }
          />

          {/* Phase 4 — Features & Integrations */}
          <Route
            path="/reservations"
            element={
              <ProtectedRoute requiredRole="WAITER">
                <ReservationCalendar />
              </ProtectedRoute>
            }
          />
          <Route
            path="/service-requests"
            element={
              <ProtectedRoute requiredRole="WAITER">
                <ServiceRequestDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/ratings"
            element={
              <ProtectedRoute requiredRole="MANAGER">
                <RatingManagement />
              </ProtectedRoute>
            }
          />
          <Route path="/qr-codes" element={<Navigate to="/restaurant-settings?tab=qrcodes" replace />} />
          <Route
            path="/venue-qr-codes"
            element={
              <ProtectedRoute>
                <VenueQRCodeManagement />
              </ProtectedRoute>
            }
          />

          {/* Payment management (staff) */}
          <Route
            path="/payments"
            element={
              <ProtectedRoute requiredRole="MANAGER">
                <PaymentManagement />
              </ProtectedRoute>
            }
          />

          {/* My Venue (for venue-level users) */}
          <Route
            path="/venue"
            element={
              <ProtectedRoute>
                <VenueSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/venue/dashboard"
            element={
              <ProtectedRoute>
                <VenueDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/venue/orders"
            element={
              <ProtectedRoute>
                <VenueOrderManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/venue/restaurants"
            element={
              <ProtectedRoute>
                <VenueRestaurantsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/venue/staff"
            element={
              <ProtectedRoute>
                <VenueStaffPage />
              </ProtectedRoute>
            }
          />
          <Route path="/venue-settings" element={<Navigate to="/venue" replace />} />

          {/* Restaurant management — entered from venue context via "Verwalten" */}
          <Route
            path="/restaurant/:restaurantId/dashboard"
            element={<ProtectedRoute requiredRole="CASHIER"><Dashboard /></ProtectedRoute>}
          />
          <Route
            path="/restaurant/:restaurantId/orders"
            element={<ProtectedRoute requiredRole="CASHIER"><OrderManagement /></ProtectedRoute>}
          />
          <Route
            path="/restaurant/:restaurantId/menu"
            element={<ProtectedRoute requiredRole="MANAGER"><MenuManagement /></ProtectedRoute>}
          />
          <Route
            path="/restaurant/:restaurantId/tables"
            element={<ProtectedRoute requiredRole="WAITER"><TableManagement /></ProtectedRoute>}
          />
          <Route
            path="/restaurant/:restaurantId/staff"
            element={<ProtectedRoute requiredRole="MANAGER"><UserManagement /></ProtectedRoute>}
          />
          <Route
            path="/restaurant/:restaurantId/qr-codes"
            element={<ProtectedRoute requiredRole="MANAGER"><QRCodeManagement /></ProtectedRoute>}
          />
          <Route
            path="/restaurant/:restaurantId/settings"
            element={<ProtectedRoute requiredRole="MANAGER"><RestaurantSettings /></ProtectedRoute>}
          />

          {/* Venue management — parameterized (enables hard navigation) */}
          <Route path="/venue/:venueId/dashboard" element={<ProtectedRoute><VenueDashboard /></ProtectedRoute>} />
          <Route path="/venue/:venueId/orders" element={<ProtectedRoute><VenueOrderManagement /></ProtectedRoute>} />
          <Route path="/venue/:venueId/restaurants" element={<ProtectedRoute><VenueRestaurantsPage /></ProtectedRoute>} />
          <Route path="/venue/:venueId/staff" element={<ProtectedRoute><VenueStaffPage /></ProtectedRoute>} />
          <Route path="/venue/:venueId/qr-codes" element={<ProtectedRoute><VenueQRCodeManagement /></ProtectedRoute>} />
          <Route path="/venue/:venueId/settings" element={<ProtectedRoute><VenueSettings /></ProtectedRoute>} />

          {/* SYSTEM admin pages */}
          <Route
            path="/venues"
            element={
              <ProtectedRoute requiredRole="SYSTEM">
                <VenueManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/venues/:venueId/orders"
            element={
              <ProtectedRoute requiredRole="SYSTEM">
                <VenueOrderManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/venues/:venueId/dashboard"
            element={
              <ProtectedRoute requiredRole="SYSTEM">
                <VenueDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/restaurants"
            element={
              <ProtectedRoute requiredRole="SYSTEM">
                <RestaurantManagement />
              </ProtectedRoute>
            }
          />
          <Route
            path="/restaurant-types"
            element={
              <ProtectedRoute requiredRole="SYSTEM">
                <RestaurantTypeManagement />
              </ProtectedRoute>
            }
          />
          {/* Public / customer-facing */}
          <Route path="/modules" element={<ReserveLandingPage />} />
          <Route path="/discover" element={<ReservationWidget />} />
          <Route path="/discover/manage/:id" element={<ReservationManage />} />
          <Route path="/reserve" element={<Navigate to="/discover" replace />} />
          <Route path="/reserve/start" element={<Navigate to="/modules" replace />} />
          <Route path="/reserve/manage/:id" element={<ReservationManage />} />
          <Route path="/order" element={<CustomerShell />}>
            <Route index element={<TableOrdering />} />
            <Route path="history" element={<CustomerOrderHistory />} />
            <Route path="account" element={<CustomerAccount />} />
            <Route path="favorites" element={<CustomerFavorites />} />
            <Route path="loyalty" element={<CustomerLoyalty />} />
          </Route>
          <Route path="/venuesordner" element={<VenueOrdering />} />
          <Route path="/venue-pickup/:venueId" element={<ProtectedRoute><VenuePickupDisplay /></ProtectedRoute>} />
          <Route path="/landingpage" element={<LandingPage />} />
          <Route path="/counter" element={<ProtectedRoute><CounterOrdering /></ProtectedRoute>} />
          <Route path="/tabletview" element={<Navigate to="/counter" replace />} />
          <Route path="/kiosk" element={<ProtectedRoute><KioskOrdering /></ProtectedRoute>} />
          <Route path="/venue-kiosk" element={<VenueKioskOrdering />} />

          {/* 404 */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        </ErrorBoundary>
        </Box>
        </Suspense>

        {!(
          matchesRoute('/venuesordner', pathname) ||
          matchesRoute('/order', pathname)
        ) && (
          <CartDialog />
        )}


        <OrderSummaryDialog />

        <VenueOrderSummaryDialog />
      </Box>

    </>
  );
}

function ThemeSync() {
  const themeMode = useUiStore((s) => s.themeMode);
  const { mode, setMode } = useColorScheme();

  useEffect(() => {
    if (themeMode && ['light', 'dark', 'system'].includes(themeMode) && themeMode !== mode) {
      try { setMode(themeMode); } catch { /* ignore */ }
    }
  }, [themeMode, mode, setMode]);

  return null;
}

export default function App() {
  const [direction, setDirection] = useState(() => {
    const savedLng = localStorage.getItem('i18nextLng') || 'de';
    return isRtlLanguage(savedLng) ? 'rtl' : 'ltr';
  });

  const theme = useMemo(() => createAppTheme(direction), [direction]);

  useEffect(() => {
    document.documentElement.dir = direction;
  }, [direction]);

  // Listen for language changes and update direction accordingly
  useEffect(() => {
    const handleLanguageChanged = (lng) => {
      const newDir = isRtlLanguage(lng) ? 'rtl' : 'ltr';
      setDirection(newDir);
    };

    // Use i18next singleton directly (configured by i18n/index.js at import time)
    import('i18next').then((mod) => {
      const i18nInstance = mod.default;
      i18nInstance.on('languageChanged', handleLanguageChanged);
    });

    return () => {
      import('i18next').then((mod) => {
        mod.default.off('languageChanged', handleLanguageChanged);
      });
    };
  }, []);

  const primaryColor = useAuthStore((s) => s.primaryColor);
  const secondaryColor = useAuthStore((s) => s.secondaryColor);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ThemeSync />
      <ThemeBranding primaryColor={primaryColor} secondaryColor={secondaryColor} />
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </ThemeProvider>
  );
}
